import { type Dispatch, type FC, type MutableRefObject, type SetStateAction, useMemo } from 'react'
import cn from 'classnames'
import { EditOutlined } from '@ant-design/icons'
import type { Field, LeadState } from '@/api/types/crm/crm.types'
import type { TrafficChannelShort } from '@/api/types/crm/crm.types'
import type { DealUtm, DealUtmKey } from './dealUtm.utils'
import Preloader from '@UI/Preloader'
import { IcoRemove } from '@/utils/icons'
import { useCRMContext } from '@pages/Admin/CRM/CRMContext'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import gclasses from '../../../../CRMSettings/global.module.css'
import TrafficChannelBlock from '@pages/Admin/CRM/components/FunnelLeadsTable/components/TrafficChannelBlock'
import type { MessengerIdentityFieldUniqueName } from '../../../utils/messengerAccountFields'
import { getLeadFieldValue } from '../../../utils/leadFields'
import { filterCrmLeadIntegrationFields } from '../../../utils/crmLeadIntegrationFields'
import MainInfoBlock from '../blocks/MainInfoBlock/MainInfoBlock'
import LeadFunnelMetaRow from '../blocks/MainInfoBlock/LeadFunnelMetaRow'
import LeadIntegrationFieldsBlock from '../blocks/MainInfoBlock/LeadIntegrationFieldsBlock/LeadIntegrationFieldsBlock'
import StatusListBlock from '../blocks/StatusListBlock/StatusListBlock'
import { LeadStatusProvider } from '../blocks/StatusListBlock/LeadStatusContext'
import TabBindingStudAccount from '../components/TabBindingStudAccount/TabBindingStudAccount'
import LeadProfileModalBlock from '../components/LeadProfileModalBlock/LeadProfileModalBlock'
import LeadProfileModalFooter from '../LeadProfileModalFooter'
import NarrowAboutContent from '../tabs/About/NarrowAboutContent'
import WideAboutContent from '../tabs/About/WideAboutContent'
import WideShoppingContent from '../tabs/Shopping/WideShoppingContent'
import WideHistoryContent from '../tabs/History/WideHistoryContent'
import UtmTagsContent from '../tabs/Utm/UtmTagsContent'
import TasksContent from '../tabs/Tasks/TasksContent'
import LeadContractsTab from '../tabs/Contracts/LeadContractsTab'
import { useLeadRefetch } from '../LeadRefetchContext'
import { resolveLeadProfileTaskCounts } from '../leadTaskCounts.utils'
import type {
  LeadProfileLead,
  OpenModalType,
} from '../LeadProfileModal.types'
import classes from '../index.module.css'
import { getLeadProfileTabs } from './leadProfileContent.utils'
import { LeadStatusRefreshBridge } from './LeadStatusRefreshBridge'

interface LeadProfileModalBodyProps {
  lead: LeadProfileLead
  access: {
    canEditFields: boolean
    canViewControlButtons: boolean
    canDelete: boolean
  }
  isNarrowLeadHeader: boolean
  isLeadModalLoading: boolean
  selectedFunnelId: number | null
  initialFunnelIdForRefetch: number | null
  leadFields: Field[]
  formData: Record<string, string>
  selectedManagerId: number | null
  canEditManagerInCard: boolean
  canViewManagerInCard: boolean
  identityFieldErrors: Partial<Record<MessengerIdentityFieldUniqueName, string>>
  trafficChannelCurrent: TrafficChannelShort | null
  trafficChannelDraftId: number | null
  setTrafficChannelDraftId: Dispatch<SetStateAction<number | null>>
  dealUtmDraft: DealUtm
  onChangeDealUtmField: (key: DealUtmKey, value: string) => void
  selectTabKey: string
  setSelectTabKey: Dispatch<SetStateAction<string>>
  historyRefreshKey: number
  setHistoryRefreshKey: Dispatch<SetStateAction<number>>
  shoppingCurrentLeadFunnelId: number | undefined
  funnelNextContactDraftRef: MutableRefObject<string>
  dealCommentDraftRef: MutableRefObject<string>
  statusListRefreshRef: MutableRefObject<(() => void) | null>
  leadStateForWorkflow: LeadState | string | undefined
  canUseFunnels: boolean
  canRemoveFunnelsInCard: boolean
  multipleDealsEnabled: boolean
  canRemoveDealInCard: boolean
  setOpenModal: Dispatch<SetStateAction<{ type: OpenModalType }>>
  onClose: () => void
  onClickByChildren: (userId: number) => void
  onOpenVkChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenMaxChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenTelegramChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenWazzupChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  handleChangeFieldData: (uniqueName: string, value: string) => void
  handleClearIdentityFieldError: (
    uniqueName: MessengerIdentityFieldUniqueName,
  ) => void
  handleSelectManager: (managerId: number | null) => void
  handleClearManager: () => void
  syncFunnelStatusDraft: (id: number | null) => void
  syncKanbanCardAfterMutation: () => void
  handleRefetchLeadInModal: (
    funnelId?: number | null,
  ) => Promise<LeadProfileLead | undefined>
  handleRefetchLeadsOnClose: () => void
  handleMoveToInWorkClick: () => void
  handlerSaveData: () => void | Promise<void>
  draftResetKey?: number
  onRequestCancel?: () => void
}

const LeadProfileModalBody: FC<LeadProfileModalBodyProps> = ({
  lead,
  access,
  isNarrowLeadHeader,
  isLeadModalLoading,
  selectedFunnelId,
  initialFunnelIdForRefetch,
  leadFields,
  formData,
  selectedManagerId,
  canEditManagerInCard,
  canViewManagerInCard,
  identityFieldErrors,
  trafficChannelCurrent,
  trafficChannelDraftId,
  setTrafficChannelDraftId,
  dealUtmDraft,
  onChangeDealUtmField,
  selectTabKey,
  setSelectTabKey,
  historyRefreshKey,
  setHistoryRefreshKey,
  shoppingCurrentLeadFunnelId,
  funnelNextContactDraftRef,
  dealCommentDraftRef,
  statusListRefreshRef,
  leadStateForWorkflow,
  canUseFunnels,
  canRemoveFunnelsInCard,
  multipleDealsEnabled,
  canRemoveDealInCard,
  setOpenModal,
  onClose,
  onClickByChildren,
  onOpenVkChatWithLead,
  onOpenMaxChatWithLead,
  onOpenTelegramChatWithLead,
  onOpenWazzupChatWithLead,
  handleChangeFieldData,
  handleClearIdentityFieldError,
  handleSelectManager,
  handleClearManager,
  syncFunnelStatusDraft,
  syncKanbanCardAfterMutation,
  handleRefetchLeadInModal,
  handleRefetchLeadsOnClose,
  handleMoveToInWorkClick,
  handlerSaveData,
  draftResetKey = 0,
  onRequestCancel,
}) => {
  const { preloadingData } = useCRMContext()
  const {
    resolveLeadFunnelId,
    getCardInFunnelSnapshot,
    leadCardSnapshot,
    cardFunnelSnapshotEpoch,
    selectedLeadFunnelId,
    isRefetchingLeadCard,
  } = useLeadRefetch()

  const tasksTabCounts = useMemo(() => {
    void cardFunnelSnapshotEpoch
    void selectedLeadFunnelId
    return resolveLeadProfileTaskCounts({
      leadFunnelId: resolveLeadFunnelId(),
      selectedFunnelId,
      getCardInFunnelSnapshot,
      leadCardSnapshot,
      lead,
    })
  }, [
    cardFunnelSnapshotEpoch,
    getCardInFunnelSnapshot,
    lead,
    leadCardSnapshot,
    resolveLeadFunnelId,
    selectedFunnelId,
    selectedLeadFunnelId,
  ])

  const leadProfileTabs = useMemo(
    () => getLeadProfileTabs(multipleDealsEnabled, tasksTabCounts),
    [multipleDealsEnabled, tasksTabCounts],
  )

  const integrationFields = useMemo(
    () => filterCrmLeadIntegrationFields(leadFields),
    [leadFields],
  )

  const studAccountHeaderExtra = (
    <div className={classes.headerStudAccount}>
      <TabBindingStudAccount
        key={lead.user_id != null ? String(lead.user_id) : 'unbound'}
        lead={lead}
        onOpenStudentAccount={() =>
          lead.user_id && onClickByChildren(lead.user_id)
        }
        onOpenAddStudAccount={() =>
          setOpenModal({ type: 'add-stud-account' })
        }
        onRefetchLead={() => {
          setOpenModal({ type: null })
          setHistoryRefreshKey((k) => k + 1)
          void handleRefetchLeadInModal(
            selectedFunnelId ?? initialFunnelIdForRefetch,
          )
          handleRefetchLeadsOnClose()
        }}
      />
    </div>
  )

  const integrationsBlock =
    integrationFields.length > 0 ? (
      <LeadProfileModalBlock
        compact
        className={classes.rightSectionIntegrations}
        innerClassName={classes.rightSectionIntegrationsInner}
        aria-label="Интеграции и мессенджеры"
        title="Интеграции"
      >
        <div
          className={cn(
            'custom-scroll',
            classes.rightSectionIntegrationsScroll,
          )}
        >
          <LeadIntegrationFieldsBlock
            layout="sidebar"
            fields={integrationFields}
            fieldsData={formData}
            updateFieldData={handleChangeFieldData}
            disabled={!access.canEditFields || isRefetchingLeadCard}
            lead={lead}
            leadId={lead.id}
            onOpenVkChatWithLead={onOpenVkChatWithLead}
            onOpenMaxChatWithLead={onOpenMaxChatWithLead}
            onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
            onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
          />
        </div>
      </LeadProfileModalBlock>
    ) : null

  const statusHistoryList = (
    <StatusListBlock
      leadId={lead.id}
      createdAt={'created_at' in lead ? lead.created_at : null}
      dateL={getLeadFieldValue(lead, 'DATEL')}
      enteredAt={'entered_at' in lead ? lead.entered_at : null}
    />
  )

  return (
    <>
      <div className={classes.loadingWrapper}>
        <div className={cn(isLeadModalLoading && classes.loadingContentBlur)}>
          <div className={cn(mclasses.modalContainer, classes.modalContainer)}>
            <div className={mclasses.modalHeader}>
              <div className={mclasses.modalTitleContainer}>
                <span
                  className={cn(
                    mclasses.modalHeaderTitle,
                    classes.modalHeaderTitle,
                  )}
                >
                  {formData['NAME']?.trim() || '(без фио)'}
                  {access.canEditFields && (
                    <EditOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                      className={cn(gclasses.activeIcon, classes.editIcon)}
                      onClick={() => setOpenModal({ type: 'edit-name' })}
                    />
                  )}
                </span>
              </div>
              <div className={classes.modalHeaderActions}>
                {studAccountHeaderExtra}
                <div className={mclasses.closeIcon} onClick={() => onClose()}>
                  <IcoRemove />
                </div>
              </div>
            </div>

            <LeadStatusProvider
              leadId={lead.id}
              funnelId={selectedFunnelId}
              onNeedUpdateLead={handleRefetchLeadsOnClose}
            >
              <LeadStatusRefreshBridge refreshRef={statusListRefreshRef} />
              <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
                <div className={classes.headerColumnWrapper}>
                  <div className={classes.leftSection}>
                    {isNarrowLeadHeader && (
                      <div
                        className={cn(
                          classes.modalTrafficMobile,
                          classes.trafficChannelPanelIsolation,
                        )}
                      >
                        <LeadFunnelMetaRow
                          lead={lead}
                          className={classes.trafficMetaRow}
                        />
                        <TrafficChannelBlock
                          layout="panel"
                          leadId={lead.id}
                          current={trafficChannelCurrent}
                          channels={preloadingData.trafficChannels ?? []}
                          canEdit={access.canEditFields}
                          onKanbanCardSync={syncKanbanCardAfterMutation}
                          draftTrafficChannelId={trafficChannelDraftId}
                          onDraftTrafficChannelIdChange={
                            setTrafficChannelDraftId
                          }
                        />
                      </div>
                    )}
                    <MainInfoBlock
                      lead={lead}
                      fields={leadFields}
                      fieldsData={formData}
                      updateFieldData={handleChangeFieldData}
                      selectedManagerId={selectedManagerId}
                      onSelectManager={handleSelectManager}
                      onClearManager={handleClearManager}
                      canEditManager={canEditManagerInCard}
                      showManager={canViewManagerInCard}
                      deferStatusCommit
                      onFunnelStatusSelectionSync={syncFunnelStatusDraft}
                      funnelNextContactDraftRef={funnelNextContactDraftRef}
                      dealCommentDraftRef={dealCommentDraftRef}
                      onOpenVkChatWithLead={onOpenVkChatWithLead}
                      onOpenMaxChatWithLead={onOpenMaxChatWithLead}
                      onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
                      onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
                      identityFieldErrors={identityFieldErrors}
                      onClearIdentityFieldError={handleClearIdentityFieldError}
                      draftResetKey={draftResetKey}
                    />

                    {isNarrowLeadHeader ? integrationsBlock : null}

                    <div
                      className={cn(classes.rightSection, classes.onlyMobile)}
                    >
                      <div className={classes.rightSectionColumn}>
                        <div className={classes.rightSectionScroll}>
                          {statusHistoryList}
                        </div>
                      </div>
                    </div>

                    <div className={classes.customTabsWrapper}>
                      <div className={classes.tabsBar}>
                        <div
                          className={classes.tabsScroll}
                          role="tablist"
                          aria-label="Разделы карточки лида"
                        >
                          {(leadProfileTabs ?? []).map((tab) => {
                            if (!tab || tab.key == null) return null
                            const key = String(tab.key)
                            const isActive = (selectTabKey ?? undefined) === key
                            return (
                              <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={cn(classes.tabItem, {
                                  [classes.tabItemActive]: isActive,
                                })}
                                onClick={() => setSelectTabKey(key)}
                              >
                                {tab.label}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    </div>

                    {selectTabKey === '1' && (
                      <NarrowAboutContent
                        lead={lead}
                        fields={leadFields}
                        fieldsData={formData}
                        updateFieldData={handleChangeFieldData}
                        inlineTextareasWhenNarrow={isNarrowLeadHeader}
                        onOpenVkChatWithLead={onOpenVkChatWithLead}
                        onOpenMaxChatWithLead={onOpenMaxChatWithLead}
                        onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
                        onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
                      />
                    )}
                  </div>

                  <div className={classes.rightSection}>
                    <div className={classes.rightSectionColumn}>
                      {!isNarrowLeadHeader && (
                        <div
                          className={cn(
                            classes.rightSectionTraffic,
                            classes.trafficChannelPanelIsolation,
                          )}
                        >
                          <LeadFunnelMetaRow
                            lead={lead}
                            className={classes.trafficMetaRow}
                          />
                          <TrafficChannelBlock
                            layout="panel"
                            leadId={lead.id}
                            current={trafficChannelCurrent}
                            channels={preloadingData.trafficChannels ?? []}
                            canEdit={access.canEditFields}
                            onKanbanCardSync={syncKanbanCardAfterMutation}
                            draftTrafficChannelId={trafficChannelDraftId}
                            onDraftTrafficChannelIdChange={
                              setTrafficChannelDraftId
                            }
                          />
                        </div>
                      )}
                      {integrationsBlock}
                      <div className={classes.rightSectionScroll}>
                        {statusHistoryList}
                      </div>
                    </div>
                  </div>
                </div>

                {selectTabKey === '1' && !isNarrowLeadHeader && (
                  <div className={classes.wideAboutRoot}>
                    <WideAboutContent
                      lead={lead}
                      fields={leadFields}
                      fieldsData={formData}
                      updateFieldData={handleChangeFieldData}
                      onOpenVkChatWithLead={onOpenVkChatWithLead}
                      onOpenMaxChatWithLead={onOpenMaxChatWithLead}
                      onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
                      onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
                    />
                  </div>
                )}
                {selectTabKey === '2' && (
                  <WideShoppingContent
                    lead={lead}
                    currentLeadFunnelId={shoppingCurrentLeadFunnelId}
                    onKanbanCardSync={syncKanbanCardAfterMutation}
                  />
                )}
                {selectTabKey === '3' && (
                  <div className={classes.historyTabBody}>
                    <WideHistoryContent
                      lead={lead}
                      refreshKey={historyRefreshKey}
                    />
                  </div>
                )}
                {selectTabKey === '4' && (
                  <div className={classes.wideAboutRoot}>
                    <UtmTagsContent
                      fields={leadFields}
                      fieldsData={formData}
                      updateFieldData={handleChangeFieldData}
                      multipleDealsEnabled={multipleDealsEnabled}
                      dealUtmDraft={dealUtmDraft}
                      onChangeDealUtmField={onChangeDealUtmField}
                    />
                  </div>
                )}
                {selectTabKey === '5' && (
                  <div className={classes.wideAboutRoot}>
                    <TasksContent multipleDealsEnabled={multipleDealsEnabled} />
                  </div>
                )}
                {selectTabKey === '6' && (
                  <div className={classes.wideAboutRoot}>
                    <LeadContractsTab lead={lead} />
                  </div>
                )}
              </div>
            </LeadStatusProvider>
          </div>

          {(access.canViewControlButtons || access.canEditFields) && (
            <LeadProfileModalFooter
              canEditFields={access.canEditFields}
              canViewControlButtons={access.canViewControlButtons}
              canDelete={access.canDelete}
              canUseFunnels={canUseFunnels}
              canRemoveFunnels={canRemoveFunnelsInCard}
              multipleDealsEnabled={multipleDealsEnabled}
              canRemoveDeal={canRemoveDealInCard}
              leadState={leadStateForWorkflow}
              onSave={() => {
                void handlerSaveData()
              }}
              onCancel={() => {
                if (onRequestCancel) onRequestCancel()
                else onClose()
              }}
              onMoveToInWork={handleMoveToInWorkClick}
              onChangeLeadState={() =>
                setOpenModal({ type: 'change-lead-state' })
              }
              onMoveToClient={() =>
                setOpenModal({ type: 'change-lead-state-in-client' })
              }
              onOpenDeleteLead={() => setOpenModal({ type: 'delete-lead' })}
              onAddFunnel={() => setOpenModal({ type: 'add-funnel' })}
              onRemoveFunnel={() => setOpenModal({ type: 'remove-funnel' })}
              onAddDeal={() => setOpenModal({ type: 'add-deal' })}
              onRemoveDeal={() => setOpenModal({ type: 'remove-deal' })}
            />
          )}
        </div>

        {isLeadModalLoading && (
          <div className={classes.loadingOverlay}>
            <Preloader />
          </div>
        )}
      </div>
    </>
  )
}

export default LeadProfileModalBody
